import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '@/shared/model';
import { useViewInitializationStateMachineContext } from '@/shared/ui';
import { selectIsFailed, selectIsFinished } from '../../model/slice';
import type { SavedPresetRecord } from '../../model/types';

interface Params {
  metaLoaded: boolean;
  applyPreset: (preset: SavedPresetRecord) => void;
  applyVendorDefault: () => void;
  presets: SavedPresetRecord[];
}

export const usePresetInitialization = ({
  applyPreset,
  applyVendorDefault,
  presets,
  metaLoaded,
}: Params) => {
  const executed = useRef(false);
  const presetsLoaded = useAppSelector(selectIsFinished);
  const presetsFailed = useAppSelector(selectIsFailed);
  const presetsSettled = presetsLoaded || presetsFailed;
  const { canApplyDefaultView } = useViewInitializationStateMachineContext();
  const [searchParams] = useSearchParams();

  useEffect(
    () => {
      if (!presetsSettled || !metaLoaded || executed.current) {
        return;
      }

      const savedPresetId = searchParams.get('savedPreset');
      const defaultView = presets.find((preset) => {
        return preset.isDefault;
      });

      executed.current = true;

      if (savedPresetId) {
        const preset = presets.find((preset) => {
          return preset.id === savedPresetId;
        });

        if (preset) {
          applyPreset(preset);

          return;
        }
      }

      if (defaultView && canApplyDefaultView) {
        applyPreset(defaultView);
      } else {
        applyVendorDefault();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [presetsLoaded, metaLoaded, presets],
  );
};
